/**
 * The pet owner's address book.
 *
 * The API and the hooks for this existed but nothing ever rendered them: `use_address_mutations`
 * was called by no screen, so a customer could PICK a saved address during checkout while
 * having no way to create one in the first place. Everything here was only reachable from the
 * mobile app.
 *
 * The map pin is the reason this screen matters beyond CRUD. A rider gets an address in words
 * and a coordinate; the words narrow it to a street and the pin finds the gate. So the form
 * makes capturing one a single tap, and is explicit about the API's rule: both ordinates or
 * neither, never one.
 */
import { useState } from 'react';
import { Crosshair, Loader2, MapPin, Pencil, Plus, Star, Trash2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { SectionHeader } from '@/components/ui/section-header';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorState } from '@/components/ui/error-state';
import { StatusPill } from '@/components/ui/status-pill';
import { TextField } from '@/components/text-field';
import { form_error_message, field_errors_from } from '@/lib/forms';
import { use_addresses, use_address_mutations } from '@/features/user-address/hooks';
import type { UserAddress, AddressInput } from '@/features/user-address/api';

type Form = {
  label: string;
  address: string;
  landmark: string;
  city: string;
  state: string;
  pincode: string;
  phone: string;
  access_notes: string;
  /** Strings while being typed; coerced on submit. */
  latitude: string;
  longitude: string;
};

const EMPTY: Form = {
  label: '',
  address: '',
  landmark: '',
  city: '',
  state: '',
  pincode: '',
  phone: '',
  access_notes: '',
  latitude: '',
  longitude: '',
};

const PINCODE = /^[1-9][0-9]{5}$/;

function form_from(a: UserAddress): Form {
  return {
    label: a.label,
    address: a.address,
    landmark: a.landmark ?? '',
    city: a.city,
    state: a.state,
    pincode: a.pincode,
    phone: a.phone ?? '',
    access_notes: a.access_notes ?? '',
    latitude: a.latitude === null ? '' : String(a.latitude),
    longitude: a.longitude === null ? '' : String(a.longitude),
  };
}

/**
 * Validate what the server validates, so the shopper is told before a round-trip.
 *
 * The coordinate rule is the interesting one: a lone ordinate is not a partial answer but an
 * unusable one, so it is rejected here rather than surfaced as a 422 naming a field the form
 * never asked them to fill.
 */
function validate(f: Form): string | null {
  if (f.label.trim().length < 1) return 'Give this address a name, like Home or Work.';
  if (f.address.trim().length < 5) return 'Enter the full street address.';
  if (f.city.trim().length < 2) return 'City is required.';
  if (f.state.trim().length < 2) return 'State is required.';
  if (!PINCODE.test(f.pincode.trim())) return 'Pincode must be 6 digits and cannot start with 0.';

  const has_lat = f.latitude.trim() !== '';
  const has_lng = f.longitude.trim() !== '';
  if (has_lat !== has_lng) {
    return 'A map pin needs both latitude and longitude — tap “Use my location”, or clear both.';
  }
  if (has_lat) {
    const lat = Number(f.latitude);
    const lng = Number(f.longitude);
    if (!Number.isFinite(lat) || lat < -90 || lat > 90) return 'Latitude must be between -90 and 90.';
    if (!Number.isFinite(lng) || lng < -180 || lng > 180) return 'Longitude must be between -180 and 180.';
  }
  return null;
}

/**
 * Form → request body, and the create/update asymmetry it has to respect.
 *
 * The two server schemas differ on purpose: on CREATE the optional text fields accept a string
 * or nothing at all, while on UPDATE they additionally accept `null`, because null is how you
 * CLEAR a value you had set. So an empty box means "omit" when creating and "clear" when
 * editing — sending null on create is a 422 naming a field the shopper left blank on purpose.
 *
 * The pin follows the same rule, with the extra constraint that it is both ordinates or
 * neither; a lone one is rejected before we get here.
 */
function payload_from(f: Form, mode: 'create' | 'update'): AddressInput {
  const has_pin = f.latitude.trim() !== '' && f.longitude.trim() !== '';
  /** Empty → omitted when creating, explicitly nulled when editing. */
  const clearable = (v: string) => {
    const t = v.trim();
    if (t) return t;
    return mode === 'update' ? null : undefined;
  };
  return {
    label: f.label.trim(),
    address: f.address.trim(),
    landmark: clearable(f.landmark),
    city: f.city.trim(),
    state: f.state.trim(),
    pincode: f.pincode.trim(),
    phone: clearable(f.phone),
    access_notes: clearable(f.access_notes),
    ...(has_pin
      ? { latitude: Number(f.latitude), longitude: Number(f.longitude) }
      : mode === 'update'
        ? { latitude: null, longitude: null }
        : {}),
  };
}

export function AddressesPage() {
  const list_q = use_addresses();
  const { create, update, remove, set_default } = use_address_mutations();

  /** null = closed, 'new' = creating, otherwise the id being edited. */
  const [editing, set_editing] = useState<string | null>(null);
  const [form, set_form] = useState<Form>(EMPTY);
  const [error, set_error] = useState<string | null>(null);
  const [field_errors, set_field_errors] = useState<Record<string, string>>({});
  const [locating, set_locating] = useState(false);

  const saving = create.isPending || update.isPending;

  function open_new() {
    set_form(EMPTY);
    set_error(null);
    set_field_errors({});
    set_editing('new');
  }

  function open_edit(a: UserAddress) {
    set_form(form_from(a));
    set_error(null);
    set_field_errors({});
    set_editing(a.id);
  }

  function set<K extends keyof Form>(key: K, value: Form[K]) {
    set_form((f) => ({ ...f, [key]: value }));
  }

  function use_my_location() {
    set_error(null);
    if (!navigator.geolocation) {
      set_error('Your browser does not support location. You can paste coordinates from Google Maps instead.');
      return;
    }
    set_locating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        set_locating(false);
        // The column is Decimal(10,7), so anything beyond 7 places is discarded server-side.
        set_form((f) => ({
          ...f,
          latitude: pos.coords.latitude.toFixed(7),
          longitude: pos.coords.longitude.toFixed(7),
        }));
      },
      () => {
        set_locating(false);
        set_error('Could not get your location. Allow location access, or paste coordinates from Google Maps.');
      },
      { enableHighAccuracy: true, timeout: 10_000 },
    );
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    set_error(null);
    set_field_errors({});
    const problem = validate(form);
    if (problem) {
      set_error(problem);
      return;
    }
    try {
      if (editing === 'new') {
        await create.mutateAsync(payload_from(form, 'create'));
      } else if (editing) {
        await update.mutateAsync({ id: editing, input: payload_from(form, 'update') });
      }
      set_editing(null);
    } catch (err) {
      set_field_errors(field_errors_from(err));
      set_error(form_error_message(err, 'Could not save that address'));
    }
  }

  const addresses = list_q.data ?? [];

  return (
    <div className="flex flex-col gap-4 p-4" data-testid="addresses-page">
      <SectionHeader
        title="My addresses"
        description="Where we deliver, and where a walker or trainer comes to meet you."
        action={
          editing === null && (
            <Button variant="gradient" onClick={open_new} data-testid="address-add">
              <Plus className="h-4 w-4" /> Add address
            </Button>
          )
        }
      />

      {editing !== null && (
        <Card className="p-4">
          <form onSubmit={submit} className="flex flex-col gap-4" data-testid="address-form">
            <div className="flex items-center justify-between">
              <h3 className="font-heading text-base font-semibold">
                {editing === 'new' ? 'New address' : 'Edit address'}
              </h3>
              <Button type="button" variant="ghost" size="sm" onClick={() => set_editing(null)} data-testid="address-cancel">
                <X className="h-4 w-4" /> Cancel
              </Button>
            </div>

            {error && (
              <p className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive" data-testid="address-error">
                {error}
              </p>
            )}

            <TextField
              label="Name this address"
              name="label"
              value={form.label}
              onChange={(e) => set('label', e.target.value)}
              error={field_errors.label}
              maxLength={40}
              placeholder="Home"
              data-testid="address-label"
            />
            <TextField
              label="Street address"
              name="address"
              value={form.address}
              onChange={(e) => set('address', e.target.value)}
              error={field_errors.address}
              maxLength={500}
              placeholder="12 MG Road, Indiranagar"
              data-testid="address-line"
            />
            <TextField
              label="Landmark (optional)"
              name="landmark"
              value={form.landmark}
              onChange={(e) => set('landmark', e.target.value)}
              maxLength={200}
              placeholder="Near the park gate"
              data-testid="address-landmark"
            />

            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <TextField
                label="City"
                name="city"
                value={form.city}
                onChange={(e) => set('city', e.target.value)}
                error={field_errors.city}
                data-testid="address-city"
              />
              <TextField
                label="State"
                name="state"
                value={form.state}
                onChange={(e) => set('state', e.target.value)}
                error={field_errors.state}
                data-testid="address-state"
              />
              <TextField
                label="Pincode"
                name="pincode"
                value={form.pincode}
                onChange={(e) => set('pincode', e.target.value.replace(/\D/g, '').slice(0, 6))}
                error={field_errors.pincode}
                inputMode="numeric"
                placeholder="560038"
                data-testid="address-pincode"
              />
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <TextField
                label="Contact number (optional)"
                name="phone"
                value={form.phone}
                onChange={(e) => set('phone', e.target.value)}
                maxLength={20}
                placeholder="+919876543210"
                data-testid="address-phone"
              />
              <TextField
                label="How to find you (optional)"
                name="access_notes"
                value={form.access_notes}
                onChange={(e) => set('access_notes', e.target.value)}
                maxLength={500}
                placeholder="Gate code 4321, 2nd floor"
                data-testid="address-access-notes"
              />
            </div>

            <div className="rounded-lg border border-border bg-card/60 p-4">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <div>
                  <Label>Map pin</Label>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Optional, but it is what gets a rider to your door rather than your street.
                    Easiest while you are standing there.
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={use_my_location}
                    disabled={locating}
                    data-testid="address-use-location"
                  >
                    {locating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Crosshair className="h-4 w-4" />}
                    {locating ? 'Locating…' : 'Use my location'}
                  </Button>
                  {(form.latitude || form.longitude) && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      // Clears BOTH: the API rejects a lone ordinate, so a "clear" that left one
                      // behind would produce a 422 the shopper could not act on.
                      onClick={() => set_form((f) => ({ ...f, latitude: '', longitude: '' }))}
                      data-testid="address-clear-pin"
                    >
                      Clear pin
                    </Button>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <TextField
                  label="Latitude"
                  name="latitude"
                  value={form.latitude}
                  onChange={(e) => set('latitude', e.target.value)}
                  error={field_errors.latitude}
                  placeholder="12.9716"
                  data-testid="address-latitude"
                />
                <TextField
                  label="Longitude"
                  name="longitude"
                  value={form.longitude}
                  onChange={(e) => set('longitude', e.target.value)}
                  error={field_errors.longitude}
                  placeholder="77.5946"
                  data-testid="address-longitude"
                />
              </div>
            </div>

            <div>
              <Button type="submit" variant="gradient" disabled={saving} data-testid="address-save">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                {editing === 'new' ? 'Save address' : 'Save changes'}
              </Button>
            </div>
          </form>
        </Card>
      )}

      {list_q.isPending ? (
        <div className="flex justify-center py-10">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : list_q.isError ? (
        <ErrorState title="Couldn't load your addresses" description="Try again in a moment." />
      ) : addresses.length === 0 ? (
        editing === null && (
          <EmptyState
            title="No saved addresses yet"
            description="Add one and it'll be offered whenever you book a walk or place an order."
            data-testid="addresses-empty"
          />
        )
      ) : (
        <Card className="p-4">
          <ul className="divide-y divide-border" data-testid="addresses-list">
            {addresses.map((a) => (
              <li key={a.id} className="flex items-start gap-3 py-3" data-testid={`address-${a.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-semibold">{a.label}</p>
                    {a.is_default && (
                      <StatusPill tone="info" data-testid={`address-default-${a.id}`}>
                        Default
                      </StatusPill>
                    )}
                    {a.latitude !== null && a.longitude !== null && (
                      /* Shown because a pin is invisible otherwise, and its absence is
                         actionable — it is the difference between the gate and the street. */
                      <span
                        className="inline-flex items-center gap-1 text-xs text-success"
                        data-testid={`address-has-pin-${a.id}`}
                        title={`${a.latitude}, ${a.longitude}`}
                      >
                        <MapPin className="h-3 w-3" /> Pinned
                      </span>
                    )}
                  </div>
                  <p className="truncate text-sm text-muted-foreground">
                    {[a.address, a.landmark, a.city, a.state, a.pincode].filter(Boolean).join(', ')}
                  </p>
                  {a.phone && <p className="text-xs text-muted-foreground">{a.phone}</p>}
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  {!a.is_default && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => set_default.mutate(a.id)}
                      title="Make default"
                      aria-label={`Make ${a.label} the default address`}
                      data-testid={`address-make-default-${a.id}`}
                    >
                      <Star className="h-4 w-4" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => open_edit(a)}
                    title="Edit"
                    aria-label={`Edit ${a.label}`}
                    data-testid={`address-edit-${a.id}`}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => remove.mutate(a.id)}
                    title="Delete"
                    aria-label={`Delete ${a.label}`}
                    data-testid={`address-delete-${a.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}
